import { useState, useEffect, useRef } from 'react';
import { JOI_API_URL } from '../constants';
import { atlasApi } from '../../../services/api/atlas.api';
import { useClickOutside } from '../../../hooks/useClickOutside';

// isAtlasSelected: whether Atlas is currently part of the source multi-select.
// Passed in so this hook can reset its own Atlas chat mode state whenever
// Atlas is removed from the selection — kept here rather than in useDeepSearch
// (which owns selectedSources) since the mode itself is purely an Atlas concern.
export const useAtlasIntelligence = (isAtlasSelected: boolean) => {
  const [atlasEnabled, setAtlasEnabled] = useState(false);
  // Whether checkAtlasAccess has finished. Until then atlasEnabled is false
  // only because access is still unknown, not because it was denied — which
  // matters to a chat restored before the check returns (e.g. on refresh).
  const [atlasAccessChecked, setAtlasAccessChecked] = useState(false);

  // Atlas chat mode — 'normal' (default) vs 'deep_insights', picked from the
  // flyout nested under Atlas Lead Intelligence in the Source list. Only
  // meaningful while source === 'atlas'.
  const [atlasDeepInsights, setAtlasDeepInsights] = useState(false);
  const [isAtlasModeMenuOpen, setIsAtlasModeMenuOpen] = useState(false);
  const atlasModeMenuRef = useRef<HTMLDivElement>(null);
  // The Atlas row in the Source menu that toggles the flyout; counts as
  // inside so it can close it.
  const atlasModeMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const checkAtlasAccess = async (email: string) => {
    if (!JOI_API_URL || !email) {
      setAtlasEnabled(false);
      setAtlasAccessChecked(true);
      return;
    }
    try {
      const data = await atlasApi.getUserId(email);
      if (!data) {
        setAtlasEnabled(false);
        return;
      }
      setAtlasEnabled(!!data?.user_id);
    } catch (e) {
      console.error('[atlas] checkAtlasAccess error:', e);
      setAtlasEnabled(false);
    } finally {
      setAtlasAccessChecked(true);
    }
  };

  // Reset Atlas mode back to the 'normal' default whenever Atlas isn't
  // selected, so re-selecting Atlas later always starts from 'normal'.
  useEffect(() => {
    if (!isAtlasSelected) {
      setAtlasDeepInsights(false);
      setIsAtlasModeMenuOpen(false);
    }
  }, [isAtlasSelected]);

  useClickOutside(
    [atlasModeMenuRef, atlasModeMenuTriggerRef],
    () => setIsAtlasModeMenuOpen(false),
    isAtlasModeMenuOpen
  );

  return {
    atlasEnabled,
    atlasAccessChecked,
    checkAtlasAccess,
    atlasDeepInsights,
    setAtlasDeepInsights,
    isAtlasModeMenuOpen,
    setIsAtlasModeMenuOpen,
    atlasModeMenuRef,
    atlasModeMenuTriggerRef,
  };
};
